import { useCallback, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { useGetCurrentUser } from "@/hooks/queries/useGetCurrentUser";
import { petsQueryKey, useGetPets } from "@/hooks/queries/useGetPets";
import { useGetTaggableSameCompanyUsers } from "@/hooks/queries/useGetTaggableSameCompanyUsers";
import { WEEKLY_INTEREST_GOAL, WEEKLY_SHORTLIST_GOAL } from "@/lib/constants";
import { stafferPets } from "@/lib/stafferPets";
import {
  type Pet,
  type StafferPetCelebration,
  type StafferPetRewardType,
} from "@/types";

export const STAFFER_PET_REWARD_EVENT = "staffer-pet-reward";
// Asks whichever component owns the pet dialogs to open the shop on a tab —
// fired from surfaces (like the sidebar) that live outside that tree.
export const STAFFER_PET_OPEN_SHOP_EVENT = "staffer-pet-open-shop";
export const STAFFER_PET_OPEN_PLAYYARD_EVENT = "staffer-pet-open-playyard";

export const useStafferPet = () => {
  const currentUser = useGetCurrentUser();
  const sameCompanyUsersQuery = useGetTaggableSameCompanyUsers();
  const petsQuery = useGetPets();

  const petData = useMemo(() => {
    const currentUserEntry =
      sameCompanyUsersQuery.data?.find(
        (user) =>
          user.id === currentUser?.id || user.email === currentUser?.email,
      ) ?? null;
    const pet =
      petsQuery.data?.find((entry) => entry.userId === currentUser?.id) ?? null;

    return {
      coinBalance: pet?.coinBalance ?? 0,
      currentUser,
      currentUserEntry,
      pet,
      totalCoinsEarned: pet?.totalCoinsEarned ?? 0,
      unlockedColorIds:
        pet === null ? [] : stafferPets.getUnlockedExclusiveColorIds(pet),
    };
  }, [currentUser, petsQuery.data, sameCompanyUsersQuery.data]);

  return {
    ...petData,
    isPending: petsQuery.isPending || sameCompanyUsersQuery.isPending,
    pets: petsQuery.data,
    sameCompanyUsers: sameCompanyUsersQuery.data,
  };
};

export const STAFFER_PET_REWARD_DETAILS: Record<
  StafferPetRewardType,
  {
    amount: number;
    celebration: StafferPetCelebration;
    description: string;
    label: string;
  }
> = {
  SEARCH_V2_SHORTLIST: {
    amount: 1,
    celebration: "single",
    description: "Search shortlist",
    label: "Search shortlist",
  },
  SUBMIT: {
    amount: 3,
    celebration: "single",
    description: "New submit",
    label: "Submit",
  },
  PLACEMENT: {
    amount: 25,
    celebration: "triple",
    description: "New placement",
    label: "Placement",
  },
  WEEKLY_INTEREST_GOAL: {
    amount: 10,
    celebration: "triple",
    description: `Weekly search goal: ${String(WEEKLY_INTEREST_GOAL)} interests`,
    label: `${String(WEEKLY_INTEREST_GOAL)} interests in a week`,
  },
  WEEKLY_SHORTLIST_GOAL: {
    amount: 15,
    celebration: "triple",
    description: `Weekly search goal: ${String(WEEKLY_SHORTLIST_GOAL)} shortlists`,
    label: `${String(WEEKLY_SHORTLIST_GOAL)} shortlists in a week`,
  },
};

export const STAFFER_PET_SEARCH_SUBMIT_REWARD = {
  amount: 10,
  celebration: "single",
  description: "Laborup Search submit",
  label: "Laborup Search submit",
} as const;

// The server awards coins now (StafferPetRewardsService in guac), so the app
// only plays the celebration for an award that came back on the mutation that
// earned it. The amounts above are kept for display copy — the shop's "how to
// earn" list and notification totals — not for awarding.
export interface StafferPetAward {
  celebration: StafferPetCelebration;
  coinsAwarded: number;
}

export const useCelebrateStafferPetAward = () => {
  /* Database CRUD Operations */
  // Read
  const currentUser = useGetCurrentUser();
  const queryClient = useQueryClient();

  /* Handlers */
  return useCallback(
    (award: StafferPetAward | null | undefined) => {
      if (!award) return;

      // Guac already awarded the coins; mirror them into the cached pet so the
      // balance moves with the celebration instead of waiting on a refetch.
      queryClient.setQueryData<Pet[]>(petsQueryKey, (pets) =>
        pets?.map((pet) => {
          if (pet.userId !== currentUser?.id) return pet;
          return {
            ...pet,
            coinBalance: pet.coinBalance + award.coinsAwarded,
            totalCoinsEarned: pet.totalCoinsEarned + award.coinsAwarded,
          };
        }),
      );

      window.dispatchEvent(
        new CustomEvent<StafferPetCelebration>(STAFFER_PET_REWARD_EVENT, {
          detail: award.celebration,
        }),
      );
    },
    [currentUser, queryClient],
  );
};
